import httpClient from 'api/httpClient';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { FilePreview, UploadedFileResponse } from 'models/File';
import {
  ChatSessionFile,
  MessageLocation,
  MessageType,
  ReplySuggestionsTracking,
  SessionType,
  UserConnection,
} from 'models/Message';
import { PaginationParams, PaginatedData } from 'models/PaginatedData';
import { ReactionType } from 'models/Reactions';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';

const sendMesssage = (
  sessionId: string,
  text: string,
  uploadedFiles: DropzoneFile[],
  messageLocation: MessageLocation,
  replySuggestionsTracking?: ReplySuggestionsTracking,
) => {
  return httpClient.post<
    MessageType & { createdChatSessionFiles: ChatSessionFile[] }
  >(`/chats/sessions/${sessionId}/messages`, {
    text,
    uploadedFiles,
    messageLocation,
    ...replySuggestionsTracking,
  });
};

const dismissReplySuggestions = (sessionId: string) => {
  return httpClient.delete(`/chats/sessions/${sessionId}/reply-suggestions`);
};

const getSessions = (
  params: PaginationParams & {
    openSessionId?: string;
    listingId?: string;
    hasUnreadMessages?: boolean;
    isExternalCommunication?: boolean;
  },
) => {
  return httpClient.get<PaginatedData<SessionType>>('/chats/sessions', {
    params,
  });
};

const getSessionById = (sessionId: string) => {
  return httpClient.get<SessionType>(`/chats/sessions/${sessionId}`);
};

const getUserConnections = (
  params: PaginationParams & { listingId?: string },
) => {
  return httpClient.get<PaginatedData<UserConnection>>(
    '/chats/user-connections',
    {
      params,
    },
  );
};

const getSessionMessages = (params: PaginationParams, sessionId: string) => {
  return httpClient.get<PaginatedData<MessageType>>(
    `/chats/sessions/${sessionId}/messages`,
    {
      params,
    },
  );
};

const readAllMessages = (sessionId: string) => {
  return httpClient.post(`/chats/sessions/read`, {
    sessionId,
  });
};

const unreadAllMessages = (sessionId: string) => {
  return httpClient.post(`/chats/sessions/unread`, {
    sessionId,
  });
};

const deleteMessage = (messageId: string) => {
  return httpClient.delete(`/chats/messages/${messageId}`);
};

const moveFileToDataRoom = (sessionId: string, fileId: string) => {
  return httpClient.post(
    `/chats/sessions/${sessionId}/files/${fileId}/move-to-data-room`,
  );
};

/**
 * Saves every attachment of one message in a single request and returns the created
 * Data Room nodes, which is what lets the caller offer "Manage access" for the whole
 * batch at once.
 */

const moveFilesToDataRoom = (sessionId: string, fileIds: string[]) => {
  return httpClient.post<DataRoomNode[]>(
    `/chats/sessions/${sessionId}/files/move-to-data-room`,
    { fileIds },
  );
};

type MessagesFilesParams = PaginationParams & {
  /** omit for the "All" bucket */
  isDataRoomFile?: boolean;
};

const getMessagesFiles = (sessionId: string, params: MessagesFilesParams) => {
  return httpClient.get<PaginatedData<ChatSessionFile>>(
    `/chats/sessions/${sessionId}/files`,
    { params },
  );
};

const previewMessageFile = (sessionId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `/chats/sessions/${sessionId}/files/${fileId}/preview`,
  );
};

const removeMessageFile = (
  sessionId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `/chats/sessions/${sessionId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

async function uploadFile(
  sessionId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('chatFile', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `/chats/sessions/${sessionId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function broadcastMessage(
  dealRoomId: string,
  text: string,
  sendInvitationEmail: boolean,
) {
  return httpClient.post(`chats/deal-rooms/${dealRoomId}/broadcast-message`, {
    text,
    sendInvitationEmail,
  });
}

const addMessageReaction = (messageId: string, reactionType: ReactionType) => {
  return httpClient.post<MessageType>(`chats/messages/${messageId}/reaction`, {
    reactionType,
  });
};

export default {
  sendMesssage,
  getSessions,
  getSessionById,
  getSessionMessages,
  getUserConnections,
  readAllMessages,
  unreadAllMessages,
  deleteMessage,
  moveFileToDataRoom,
  moveFilesToDataRoom,
  getMessagesFiles,
  uploadFile,
  previewMessageFile,
  removeMessageFile,
  broadcastMessage,
  addMessageReaction,
  dismissReplySuggestions,
};
